import { Empty } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import React from 'react';

import { AppButton, AppPagination, AppTable } from '@/components/atoms';
import { CheckCircleO, EditFill, Link } from '@/components/atoms/AppIcon';
import { ADMIN_LIST_DEFAULT_PER_PAGE, EMPTY_TEXT_FILTERED } from '@/constants';
import type { AdminUserListItem } from '@/services/admin/admins/types';
import { getTenantCustomization, tenantCapability } from '@customize/registry';

type SortKey = 'id' | 'name' | 'email' | 'last_login_at';
type SortOrder = 'ascend' | 'descend';

const MANAGE_SITE_LABELS: Record<string, string> = {
  'super-admin': 'スーパー管理者',
  medix: 'メディックス',
  upsystem: 'メディックス',
};

const manageSiteLabel = (siteType: string): string =>
  getTenantCustomization(siteType)?.name ?? MANAGE_SITE_LABELS[siteType] ?? siteType;

const showsSsoMapping = (record: AdminUserListItem): boolean =>
  tenantCapability(record.manage_site, 'adminSsoMapping', true);

interface AdminUsersListMeta {
  current_page: number;
  total: number;
  per_page: number;
}

interface AdminUserListTableProps {
  isSuperAdminRoute: boolean;
  rows: AdminUserListItem[];
  isLoading: boolean;
  meta: AdminUsersListMeta;
  sortKey: SortKey | null;
  sortOrder: SortOrder | null;
  onChange: (
    _p: any,
    _f: any,
    sorter: SorterResult<AdminUserListItem> | SorterResult<AdminUserListItem>[]
  ) => void;
  onPageChange: (page: number) => void;
  onEdit: (record: AdminUserListItem) => void;
  /** Bấm 「SSO連携」 để thử ghi lại identity link (BD sheet 2, mục 7.0). */
  onRetrySsoLink?: (record: AdminUserListItem) => void;
  /** id đang chạy retry, để khoá các nút còn lại và hiện spinner đúng dòng. */
  retryingSsoLinkId?: number | null;
}

const AdminUserListTable: React.FC<AdminUserListTableProps> = ({
  isSuperAdminRoute,
  rows,
  isLoading,
  meta,
  sortKey,
  sortOrder,
  onChange,
  onPageChange,
  onEdit,
  onRetrySsoLink,
  retryingSsoLinkId = null,
}) => {
  const columns = [
    {
      title: 'ID',
      key: 'id',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '管理者名',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
      sortOrder: sortKey === 'name' ? sortOrder : undefined,
      // BD sheet 2, mục 6.0: 「ở bên cạnh tên user hiển thị thêm label SSO連携済み」.
      // Chỉ hiện khi server xác nhận đã ghi được sso_identity_links — KHÔNG hiện chỉ vì
      // người quản trị đã tick chọn hệ thống (xem AdminSsoLinkStatus).
      render: (value: string, record: AdminUserListItem) => (
        <span className="inline-flex items-center gap-2">
          <span>{value}</span>
          {/*
            Hình dạng và ba mã màu lấy từ mockup BD (`image1.png`), đọc bằng pixel chứ không ước
            lượng: viên thuốc bo tròn hẳn, nền `#E1F5EE`, viền `#5DCAA5`, chữ `#256E56`, kèm icon
            vòng-tròn-dấu-tích ở bên trái.
          */}
          {showsSsoMapping(record) && record.sso_link_status === 'linked' ? (
            <span
              data-testid={`sso-linked-badge-${record.id}`}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#5DCAA5] bg-[#E1F5EE] px-2 py-0.5 text-xs font-medium text-[#256E56]"
            >
              <CheckCircleO />
              SSO連携済み
            </span>
          ) : null}
          {/*
            'pending' = the link is written on P1 and delivery to P2/P3 is queued but not applied
            yet. Deliberately a LABEL, not a button: `SyncPushJob` runs after the save response has
            already been sent, so the work is seconds from finishing on its own and pressing
            「SSO連携」 would only ask for what is already under way. Measured on dev1 2026-09-04:
            a new admin read `unlinked` at t+4s and `linked` at t+10s, untouched — those six
            seconds are exactly what QC reported as "button, then badge after a reload".
            Neutral grey, because this is waiting rather than success or failure.
          */}
          {showsSsoMapping(record) && record.sso_link_status === 'pending' ? (
            <span
              data-testid={`sso-pending-badge-${record.id}`}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#D9D9D9] bg-[#F5F5F5] px-2 py-0.5 text-xs font-medium text-[#595959]"
              title="SSO連携を処理中です。完了すると自動的に「SSO連携済み」に変わります。"
            >
              SSO連携処理中
            </span>
          ) : null}
        </span>
      ),
    },
    {
      title: 'メールアドレス',
      key: 'email',
      dataIndex: 'email',
      sorter: true,
      sortOrder: sortKey === 'email' ? sortOrder : undefined,
    },
    ...(isSuperAdminRoute
      ? [
          {
            title: 'サイト区分',
            key: 'manage_site',
            dataIndex: 'manage_site',
            render: (value: string) => manageSiteLabel(value),
          },
        ]
      : []),
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: AdminUserListItem) => (
        <div className="flex items-center gap-2">
          <AppButton
            type="primary"
            icon={<EditFill />}
            onClick={() => onEdit(record)}
            data-testid={`admin-edit-${record.id}`}
          >
            編集
          </AppButton>
          {/*
            BD sheet 2, mục 7.0: 「Khi việc đồng bộ tài khoản SSO lên Cognito thất bại, tại màn
            hình list sẽ hiển thị button liên kết SSO để user tự retry」.

            Hiện đúng khi status = 'unlinked', tức CÓ grant active nhưng CHƯA có identity link.
            'none' (không thuộc diện SSO), 'pending' (đang xử lý) và 'linked' (đã xong)
            đều không có gì để thử lại.
          */}
          {showsSsoMapping(record) && record.sso_link_status === 'unlinked' ? (
            <AppButton
              className="sso-link-button"
              icon={<Link />}
              onClick={() => onRetrySsoLink?.(record)}
              loading={retryingSsoLinkId === record.id}
              disabled={retryingSsoLinkId !== null && retryingSsoLinkId !== record.id}
              data-testid={`sso-link-retry-${record.id}`}
            >
              SSO連携
            </AppButton>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-md border">
      <AppTable
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={isLoading}
        onChange={onChange}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />
      <div className="p-4 flex justify-end">
        <AppPagination
          current={meta.current_page}
          total={meta.total}
          pageSize={meta.per_page || ADMIN_LIST_DEFAULT_PER_PAGE}
          hideOnSinglePage
          onChange={onPageChange}
        />
      </div>
    </div>
  );
};

export default AdminUserListTable;
